/**
 * The one place that edits three.js's built-in shader programs (the slice 2a
 * rule in `AGENTS.md`): `onBeforeCompile` with named three.js chunk
 * replacement, three's own extension mechanism. Other modules describe what
 * they add as a `ShaderHook` (uniforms, defines, header declarations, and GLSL
 * to put before or after a named `#include <chunk>`); this module applies every
 * hook of a material in the order they were added and derives the program
 * cache key from their keys. Nothing here ever edits GLSL a module of ours
 * wrote: a behaviour that differs is a define or a uniform of the hook. The
 * prototype did the same with a chain of `.replace()` calls per adapter
 * (`dungeon-surfaces.js`, `dungeon-radiance.js`, `engine/src/radiance.js`,
 * `engine/src/architecture-cutaway.js`).
 */
import type * as THREE from 'three';

/** A three.js shader chunk the hooks may extend (the ones slice 2a uses, and the town's of slice 6a). */
export type ShaderChunk =
  | 'common'
  | 'uv_vertex'
  | 'begin_vertex'
  | 'beginnormal_vertex'
  | 'project_vertex'
  | 'worldpos_vertex'
  | 'color_fragment'
  | 'map_fragment'
  | 'emissivemap_fragment'
  | 'alphatest_fragment'
  | 'alphamap_fragment'
  | 'lights_physical_fragment'
  | 'lights_fragment_end'
  | 'aomap_fragment'
  | 'opaque_fragment'
  | 'dithering_fragment';

export type ChunkEdit = { readonly chunk: ShaderChunk; readonly before?: string; readonly after?: string };

export type ShaderHook = {
  /** Part of the program cache key: two hooks with the same key must produce the same GLSL. */
  readonly key: string;
  readonly uniforms?: Readonly<Record<string, THREE.IUniform>>;
  readonly defines?: Readonly<Record<string, string>>;
  /** Declarations put before the vertex and fragment programs' own code. */
  readonly vertexHeader?: string;
  readonly fragmentHeader?: string;
  readonly vertex?: readonly ChunkEdit[];
  readonly fragment?: readonly ChunkEdit[];
};

type Shader = THREE.WebGLProgramParametersWithUniforms;

const hooks = new WeakMap<THREE.Material, ShaderHook[]>();

const edit = (source: string, edits: readonly ChunkEdit[] | undefined, stage: string): string => {
  let out = source;
  for (const e of edits ?? []) {
    const include = `#include <${e.chunk}>`;
    if (!out.includes(include)) throw new Error(`material hook: the ${stage} program has no ${include}`);
    out = out.replace(include, `${e.before ?? ''}\n${include}\n${e.after ?? ''}`);
  }
  return out;
};

const apply = (list: readonly ShaderHook[], shader: Shader): void => {
  const defines = list.flatMap((h) => Object.entries(h.defines ?? {}).map(([k, v]) => `#define ${k} ${v}`)).join('\n');
  shader.vertexShader = `${defines}\n${list.map((h) => h.vertexHeader ?? '').join('\n')}\n${shader.vertexShader}`;
  shader.fragmentShader = `${defines}\n${list.map((h) => h.fragmentHeader ?? '').join('\n')}\n${shader.fragmentShader}`;
  for (const h of list) {
    Object.assign(shader.uniforms, h.uniforms ?? {});
    shader.vertexShader = edit(shader.vertexShader, h.vertex, 'vertex');
    shader.fragmentShader = edit(shader.fragmentShader, h.fragment, 'fragment');
  }
};

/** The keys of a material's hooks, in the order they apply (tests probe a finish with it). */
export const hookKeys = (material: THREE.Material): readonly string[] => (hooks.get(material) ?? []).map((h) => h.key);

/** Adds a hook to a material; hooks apply in the order added. The material recompiles on next use. */
export const addShaderHook = (material: THREE.Material, hook: ShaderHook): void => {
  const list = hooks.get(material);
  if (list !== undefined) {
    list.push(hook);
  } else {
    const fresh = [hook];
    hooks.set(material, fresh);
    material.onBeforeCompile = (shader) => {
      apply(fresh, shader);
    };
    material.customProgramCacheKey = () => fresh.map((h) => h.key).join('/');
  }
  material.needsUpdate = true;
};

/** Gives `to` the hooks `from` has, in the same order (a material cloned for a fading part keeps its surface's shader additions; the prototype's `userData.install`). */
export const copyShaderHooks = (from: THREE.Material, to: THREE.Material): void => {
  for (const hook of hooks.get(from) ?? []) addShaderHook(to, hook);
};
